import {
  Actor,
  Collider,
  Falling,
  Instability,
  Kit,
  Projectile,
  Static,
  Transform,
  Velocity,
} from '@/components'
import type { EventBus } from '@/core/EventBus'
import type { Entity } from '@/core/ecs/Entity'
import type { SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { BOLT, PUSH, launchSpeed } from '@/game/abilities/definitions'
import { applyKnockback } from '@/game/abilities/handlers'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, worldScale } from '@/game/resources'

/**
 * Flight, bounces, ricochets and impact of the plasma bolt.
 *
 * The bolt is what turns facing into an aim and obstacles into tools: it goes
 * where you were looking, skips off the slab, and reflects off props by mirror
 * about the contact normal — so a shot around a crate is a thing you can plan.
 * Crucially, a fighter it hits is launched **along the bolt's own heading**, not
 * away from the caster, which is what makes a ricochet mean something.
 *
 * Everything here is **swept**, not sampled: at 50 m/s the bolt covers 0.83 m
 * per fixed step, and a fighter is only 1.2 m of capture window — a
 * point-in-circle test at each step would let it pass straight through people
 * and clip the corners of props. Sweeping tests the whole segment travelled,
 * so speed can go up without the shot becoming unreliable.
 *
 * See docs/PHYSICS.md §6.2а.
 */
export function projectileSystem({ world, dt }: SystemContext): void {
  const arena = world.resource(ArenaResource)
  const events = world.resource(EventsResource)

  for (const e of world.query([Projectile, Transform, Velocity]).entities()) {
    const bolt = world.read(e, Projectile)
    const tr = world.read(e, Transform)
    const vel = world.read(e, Velocity)

    bolt.life -= dt
    bolt.selfGrace -= dt
    if (bolt.life <= 0) {
      expire(world, events, e, tr)
      continue
    }

    const fromX = tr.x
    const fromZ = tr.z

    vel.y -= BOLT.gravity * dt
    tr.x += vel.x * dt
    tr.y += vel.y * dt
    tr.z += vel.z * dt

    // Skip off the slab.
    if (tr.y <= BOLT.floorY) {
      tr.y = BOLT.floorY
      vel.y = Math.abs(vel.y) * BOLT.bounce
      vel.x *= BOLT.skid
      vel.z *= BOLT.skid
      bolt.bounces++
      events.emit(Events.BoltBounce, {
        entity: e,
        player: bolt.player,
        x: tr.x,
        z: tr.z,
        kind: 'floor',
      })
    }

    // The crescent faces where it is going, including after a ricochet.
    if (Math.abs(vel.x) + Math.abs(vel.z) > 1e-4) tr.angle = Math.atan2(vel.x, vel.z)

    const dx = tr.x - fromX
    const dz = tr.z - fromZ

    // Whichever comes first along the segment wins: a bolt that would ricochet
    // off a crate cannot also hit the fighter standing behind it.
    const prop = sweepProps(world, bolt, fromX, fromZ, dx, dz)
    const victim = sweepFighters(world, bolt, fromX, fromZ, dx, dz)

    if (victim && (!prop || victim.t <= prop.t)) {
      hitFighter(world, e, bolt, victim, vel)
      continue
    }
    if (prop) {
      ricochet(events, e, bolt, tr, vel, fromX, fromZ, dx, dz, prop)
      continue
    }

    // Off the platform: the bolt falls into the void with everything else.
    if (!arena.contains(tr.x, tr.z)) expire(world, events, e, tr)
  }
}

interface Contact {
  /** Where along this step's segment the contact happened, 0..1. */
  t: number
  nx: number
  nz: number
}

interface VictimContact extends Contact {
  entity: Entity
  x: number
  z: number
}

/**
 * Earliest point at which the swept bolt touches a circle, or null.
 *
 * Solves |p + t·d − c|² = r² and keeps the near root. `t` slightly below zero is
 * accepted so a bolt that starts the step already overlapping — the frame after
 * a ricochet, say — still registers.
 */
function sweepCircle(
  fromX: number,
  fromZ: number,
  dx: number,
  dz: number,
  cx: number,
  cz: number,
  radius: number,
): number | null {
  const ox = fromX - cx
  const oz = fromZ - cz
  const a = dx * dx + dz * dz
  if (a < 1e-9) return ox * ox + oz * oz <= radius * radius ? 0 : null
  const b = 2 * (ox * dx + oz * dz)
  const c = ox * ox + oz * oz - radius * radius
  const disc = b * b - 4 * a * c
  if (disc < 0) return null
  const root = Math.sqrt(disc)
  const t0 = (-b - root) / (2 * a)
  const t1 = (-b + root) / (2 * a)
  if (t0 >= -0.001 && t0 <= 1) return Math.max(0, t0)
  if (t1 >= -0.001 && t1 <= 1) return Math.max(0, t1)
  // Started inside and ends inside: still a contact, at the start.
  return c < 0 ? 0 : null
}

/** The static body the bolt meets first this step, with its contact normal. */
function sweepProps(
  world: World,
  bolt: ReturnType<typeof Projectile.create>,
  fromX: number,
  fromZ: number,
  dx: number,
  dz: number,
): Contact | null {
  let best: Contact | null = null

  for (const solid of world.query([Transform, Collider, Static]).entities()) {
    const st = world.read(solid, Transform)
    const sc = world.read(solid, Collider)

    if (sc.shape === 'circle') {
      const t = sweepCircle(fromX, fromZ, dx, dz, st.x, st.z, sc.radius + bolt.radius)
      if (t === null || (best && t >= best.t)) continue
      const hx = fromX + dx * t - st.x
      const hz = fromZ + dz * t - st.z
      const len = Math.hypot(hx, hz) || 1
      best = { t, nx: hx / len, nz: hz / len }
      continue
    }

    // Rotated box: work in its own frame, where it is an AABB and the slab
    // test gives both the entry time and the face that was crossed.
    const cos = Math.cos(sc.angle)
    const sin = Math.sin(sc.angle)
    const px = (fromX - st.x) * cos + (fromZ - st.z) * sin
    const pz = -(fromX - st.x) * sin + (fromZ - st.z) * cos
    const vx = dx * cos + dz * sin
    const vz = -dx * sin + dz * cos
    const hx = sc.halfX + bolt.radius
    const hz = sc.halfZ + bolt.radius

    const slabX = slab(px, vx, hx)
    const slabZ = slab(pz, vz, hz)
    if (!slabX || !slabZ) continue
    const enter = Math.max(slabX.enter, slabZ.enter)
    const exit = Math.min(slabX.exit, slabZ.exit)
    if (enter > exit || exit < 0 || enter > 1) continue

    const t = Math.max(0, enter)
    if (best && t >= best.t) continue
    // The later of the two entries is the axis actually crossed.
    const localX = slabX.enter > slabZ.enter ? -Math.sign(vx) || 1 : 0
    const localZ = slabX.enter > slabZ.enter ? 0 : -Math.sign(vz) || 1
    best = {
      t,
      nx: localX * cos - localZ * sin,
      nz: localX * sin + localZ * cos,
    }
  }

  return best
}

/** Entry and exit times of a ray against one pair of parallel planes. */
function slab(p: number, v: number, half: number): { enter: number; exit: number } | null {
  if (Math.abs(v) < 1e-9) return Math.abs(p) <= half ? { enter: -Infinity, exit: Infinity } : null
  const t0 = (-half - p) / v
  const t1 = (half - p) / v
  return { enter: Math.min(t0, t1), exit: Math.max(t0, t1) }
}

/**
 * The first fighter the swept crescent touches.
 *
 * The crescent is a **front**, not a point: a bar `2 · halfWidth` across, held
 * perpendicular to its heading. So the test is a swept rectangle — work in the
 * bolt's own frame, where "did it pass through them" is two interval checks —
 * rather than a circle, which is what made a shot that visibly crossed someone
 * fly straight past them.
 */
function sweepFighters(
  world: World,
  bolt: ReturnType<typeof Projectile.create>,
  fromX: number,
  fromZ: number,
  dx: number,
  dz: number,
): VictimContact | null {
  const travel = Math.hypot(dx, dz)
  if (travel < 1e-6) return null
  const fx = dx / travel
  const fz = dz / travel

  let best: VictimContact | null = null

  for (const target of world
    .query([Actor, Transform, Collider, Velocity], { none: [Falling] })
    .entities()) {
    if (target === bolt.owner && bolt.selfGrace > 0) continue
    const t = world.read(target, Transform)
    const col = world.read(target, Collider)

    const rx = t.x - fromX
    const rz = t.z - fromZ
    // Along the heading, and across it.
    const along = rx * fx + rz * fz
    const across = Math.abs(-rx * fz + rz * fx)

    if (across > bolt.halfWidth + col.radius) continue
    // The front sweeps from 0 to `travel` this step, and is `radius` thick.
    const reach = bolt.radius + col.radius
    if (along < -reach || along > travel + reach) continue

    const hit = clamp01((along - reach) / travel)
    if (best && hit >= best.t) continue
    best = {
      t: hit,
      entity: target,
      // Report the contact where the front actually met them, not at the
      // crescent's centre — the effects and the launch direction hang off this.
      x: fromX + fx * Math.max(0, along) - fx * reach * 0.5,
      z: fromZ + fz * Math.max(0, along) - fz * reach * 0.5,
      nx: 0,
      nz: 0,
    }
  }

  return best
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** Launches the fighter the bolt reached, along the bolt's heading. */
function hitFighter(
  world: World,
  e: Entity,
  bolt: ReturnType<typeof Projectile.create>,
  victim: VictimContact,
  vel: ReturnType<typeof Velocity.create>,
): void {
  const speed = Math.hypot(vel.x, vel.z)
  const dirX = speed < 1e-4 ? 0 : vel.x / speed
  const dirZ = speed < 1e-4 ? 1 : vel.z / speed
  const instability = world.get(victim.entity, Instability)?.value ?? 0

  applyKnockback(
    world,
    victim.entity,
    dirX,
    dirZ,
    launchSpeed(PUSH.launch, instability, worldScale(world)) *
      (world.get(bolt.owner, Kit)?.pushPower ?? 1),
    PUSH.instability,
    'push',
    bolt.owner,
    victim.x,
    victim.z,
  )
  world.destroyEntity(e)
}

/** Mirror reflection about the contact normal, at the point of contact. */
function ricochet(
  events: EventBus,
  e: Entity,
  bolt: ReturnType<typeof Projectile.create>,
  tr: ReturnType<typeof Transform.create>,
  vel: ReturnType<typeof Velocity.create>,
  fromX: number,
  fromZ: number,
  dx: number,
  dz: number,
  contact: Contact,
): void {
  // Sit at the contact, nudged clear along the normal so the next step cannot
  // find the same surface again.
  tr.x = fromX + dx * contact.t + contact.nx * 0.03
  tr.z = fromZ + dz * contact.t + contact.nz * 0.03

  const along = vel.x * contact.nx + vel.z * contact.nz
  if (along < 0) {
    vel.x = (vel.x - 2 * along * contact.nx) * BOLT.reflect
    vel.z = (vel.z - 2 * along * contact.nz) * BOLT.reflect
  }
  bolt.bounces++
  events.emit(Events.BoltBounce, {
    entity: e,
    player: bolt.player,
    x: tr.x,
    z: tr.z,
    kind: 'prop',
  })
}

function expire(
  world: World,
  events: EventBus,
  e: Entity,
  tr: { x: number; z: number },
): void {
  events.emit(Events.BoltBounce, {
    entity: e,
    player: 0,
    x: tr.x,
    z: tr.z,
    kind: 'expire',
  })
  world.destroyEntity(e)
}
